import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  ArrowLeft, Copy, Eye,
} from 'lucide-react';
import { Field, InfoTip, inputCls, btnPrimary, btnGhost, grid3 } from '../components/shared/FormKit';
import { fetchJSON } from '../utils/api';
// Try the paper as a participant would — client-side, nothing recorded.
import QuizPreviewModal, { normalizePreviewQuestions } from '../components/lnd/QuizPreviewModal';
// The question cards and the paper's model — shared with the trainer's
// session-page editor so a paper is written the same way everywhere.
import QuizQuestionsEditor, {
  blankQuestion, fromServer, toPayload, paperProblem,
} from '../components/lnd/QuizQuestionsEditor';
import RefLink from '../components/shared/RefLink';

/**
 * Quiz editor — /ld/quizzes/new and /ld/quizzes/:id (docs/modules/ld.md §5).
 *
 * A page rather than a modal: a paper is a list of questions, each with its
 * own options, and that does not fit in a dialog. The header (title,
 * category, pass mark, description) sits on top; the questions follow as
 * cards that can be reordered, retyped and removed.
 *
 * Once anyone has sat the quiz the questions are frozen (the server refuses
 * a paper change with 409 QUIZ_LOCKED) — the header still saves, and
 * Duplicate makes a fresh copy to revise.
 */

const LDQuizEditor = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = !!id;

  const [title, setTitle] = useState('');
  const [categoryId, setCategoryId] = useState('');
  const [passMark, setPassMark] = useState('');
  const [description, setDescription] = useState('');
  const [questions, setQuestions] = useState(() => [blankQuestion()]);
  const [quiz, setQuiz] = useState(null);           // the loaded record (edit)
  const [usage, setUsage] = useState(null);
  const [categoryOptions, setCategoryOptions] = useState([]);
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [saved, setSaved] = useState(false);
  // The preview sitting runs on the questions AS CURRENTLY EDITED, saved or
  // not — so HR can try the paper before pressing Save. Null = closed.
  const [preview, setPreview] = useState(null);

  const locked = !!quiz?.locked;

  useEffect(() => {
    fetchJSON('/api/ld/training-categories/options?includeInactive=true')
      .then((d) => setCategoryOptions(d.data || []))
      .catch((e) => console.error('Error loading training categories:', e));
  }, []);

  const load = useCallback(async () => {
    if (!isEdit) return;
    setLoading(true);
    setError(null);
    try {
      const data = await fetchJSON(`/api/ld/quizzes/${id}`);
      const q = data.quiz;
      setQuiz(q);
      setUsage(data.usage || null);
      setTitle(q.title || '');
      setCategoryId(q.categoryId != null ? String(q.categoryId) : '');
      setPassMark(q.passMark != null ? String(q.passMark) : '');
      setDescription(q.description || '');
      setQuestions((q.questions || []).length > 0 ? q.questions.map(fromServer) : [blankQuestion()]);
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  }, [id, isEdit]);

  useEffect(() => { load(); }, [load]);

  // Retired categories stay pickable on a quiz that already names them.
  const pickableCategories = useMemo(() => {
    const active = categoryOptions.filter((c) => c.active !== false);
    if (categoryId && !active.some((c) => String(c.id) === String(categoryId))) {
      const own = categoryOptions.find((c) => String(c.id) === String(categoryId));
      if (own) active.push({ ...own, title: `${own.title} (inactive)` });
    }
    return active;
  }, [categoryOptions, categoryId]);

  // ── Save ───────────────────────────────────────────────────────────────────
  const save = async () => {
    setError(null);
    setSaved(false);
    if (!title.trim()) { setError('Give the quiz a title.'); return; }
    if (passMark === '' || !Number.isInteger(Number(passMark)) || Number(passMark) < 1 || Number(passMark) > 100) {
      setError('Pass mark is required — a whole number between 1 and 100.');
      return;
    }
    if (!locked) {
      const problem = paperProblem(questions);
      if (problem) { setError(problem); return; }
    }
    const header = {
      title: title.trim(),
      categoryId: categoryId ? Number(categoryId) : null,
      passMark: Number(passMark),
      description: description.trim() || null,
    };
    const paper = toPayload(questions);
    setSaving(true);
    try {
      if (isEdit) {
        await fetchJSON(`/api/ld/quizzes/${id}`, {
          method: 'PUT',
          body: JSON.stringify(locked ? header : { ...header, questions: paper }),
        });
        setSaved(true);
        load();
      } else {
        const data = await fetchJSON('/api/ld/quizzes', {
          method: 'POST', body: JSON.stringify({ ...header, questions: paper }),
        });
        navigate(`/ld/quizzes/${data.quiz.id}`, { replace: true });
      }
    } catch (e) { setError(e.message); }
    finally { setSaving(false); }
  };

  // Same checks Save runs — a half-written question would only crash the
  // sitting; the banner names the question instead.
  const openPreview = () => {
    setError(null);
    const problem = paperProblem(questions);
    if (problem) { setError(`Cannot preview yet — ${problem}`); return; }
    setPreview({
      title: title.trim() || 'Untitled quiz',
      description: description.trim() || null,
      passMark: String(passMark).trim() === '' ? null : Number(passMark),
      questions: normalizePreviewQuestions(questions),
    });
  };

  const duplicate = async () => {
    setSaving(true);
    setError(null);
    try {
      const data = await fetchJSON(`/api/ld/quizzes/${id}/duplicate`, { method: 'POST' });
      // Same route, new id: the editor is not remounted, so the flag must be
      // released here or the copy's Save stays disabled.
      navigate(`/ld/quizzes/${data.quiz.id}`);
    } catch (e) { setError(e.message); }
    setSaving(false);
  };

  const totalPoints = questions.reduce((s, q) => s + (Number(q.points) || 0), 0);

  return (
    <div className="p-4 sm:p-6 max-w-4xl mx-auto">
      <Link to="/ld/quizzes" className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-700">
        <ArrowLeft className="w-4 h-4" /> Quiz Bank
      </Link>
      <div className="mt-2 mb-4 flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-slate-900">{isEdit ? 'Edit Quiz' : 'New Quiz'}</h1>
          <p className="text-sm text-slate-500 mt-1">
            {questions.length} question{questions.length === 1 ? '' : 's'} · {totalPoints} point{totalPoints === 1 ? '' : 's'} in total
            {usage && ` · attached to ${usage.sessions} session${usage.sessions === 1 ? '' : 's'} · ${usage.attempts} sitting${usage.attempts === 1 ? '' : 's'}`}
          </p>
        </div>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={openPreview}
            disabled={loading}
            title="Sit the quiz yourself, exactly as a participant would — nothing is recorded"
            className={`${btnGhost} inline-flex items-center gap-1.5 border border-slate-300 disabled:opacity-50`}
          >
            <Eye className="w-4 h-4" /> Preview
          </button>
          {isEdit && (
            <button type="button" onClick={duplicate} disabled={saving} className={`${btnGhost} inline-flex items-center gap-1.5 border border-slate-300`}>
              <Copy className="w-4 h-4" /> Duplicate
            </button>
          )}
          <button type="button" onClick={save} disabled={saving || loading} className={btnPrimary}>
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>

      {/* Written by a trainer from the session page — the bank says where it
          came from, so a paper found here can be traced back to its course. */}
      {quiz?.sourceSessionId && (
        <div className="mb-4 p-3 bg-emerald-50 border border-emerald-200 rounded-lg text-sm text-emerald-900">
          Written by the trainers of session{' '}
          <RefLink to={`/my/sessions/${quiz.sourceSessionId}`} className="font-medium underline-offset-2 hover:underline">
            {quiz.sourceSessionCode || `#${quiz.sourceSessionId}`}
          </RefLink>
          {' '}— they can keep editing it from the session page until it is sat, or the session is marked completed.
        </div>
      )}

      {locked && (
        <div className="mb-4 p-3 bg-slate-50 border border-slate-200 rounded-lg text-sm text-slate-700">
          <p className="font-medium">
            This quiz has been sat {quiz.attemptCount} time{quiz.attemptCount === 1 ? '' : 's'} — its questions are frozen
          </p>
          <p className="mt-1 text-xs text-slate-500">
            Scores already recorded must keep meaning what they meant. The title, category, pass mark and description
            still save; to change the questions, press Duplicate and attach the copy to future sessions.
          </p>
        </div>
      )}

      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
          {error}
        </div>
      )}
      {saved && !error && (
        <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg text-green-800 text-sm">
          Saved.
        </div>
      )}

      {loading ? (
        <p className="text-sm text-slate-500">Loading…</p>
      ) : (
        <div className="space-y-4">
          {/* Header */}
          <div className="bg-white rounded-lg border border-slate-200 p-6 space-y-4">
            <Field label="Title" required>
              <input
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                className={inputCls}
                placeholder="e.g. Annual IFRS Update 2026 — knowledge check"
                maxLength={255}
              />
            </Field>
            <div className={grid3}>
              <div className="sm:col-span-2">
                <Field label="Category" hint="The same training categories sessions use.">
                  <select value={categoryId} onChange={(e) => setCategoryId(e.target.value)} className={inputCls}>
                    <option value="">No category</option>
                    {pickableCategories.map((c) => <option key={c.id} value={c.id}>{c.title}</option>)}
                  </select>
                </Field>
              </div>
              <Field
                label={<>Pass Mark (%) <InfoTip text="A sitting scoring at or above this percentage is marked Passed; below it the participant retakes." /></>}
                required
              >
                <input
                  type="number"
                  min="1"
                  max="100"
                  step="1"
                  value={passMark}
                  onChange={(e) => setPassMark(e.target.value)}
                  className={inputCls}
                  placeholder="e.g. 70"
                />
              </Field>
            </div>
            <Field label="Description" hint="Shown to the participant on the quiz's start page.">
              <textarea
                rows="2"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                className={inputCls}
                placeholder="What the quiz covers, or any instructions."
                maxLength={5000}
              />
            </Field>
          </div>

          {/* Questions */}
          <QuizQuestionsEditor questions={questions} setQuestions={setQuestions} locked={locked} />

          <div className="flex justify-end gap-2 pt-2">
            <Link to="/ld/quizzes" className={btnGhost}>Back to List</Link>
            <button
              type="button"
              onClick={openPreview}
              className={`${btnGhost} inline-flex items-center gap-1.5 border border-slate-300`}
            >
              <Eye className="w-4 h-4" /> Preview
            </button>
            <button type="button" onClick={save} disabled={saving} className={btnPrimary}>
              {saving ? 'Saving…' : 'Save'}
            </button>
          </div>
        </div>
      )}

      {preview && (
        <QuizPreviewModal
          title={preview.title}
          description={preview.description}
          passMark={preview.passMark}
          questions={preview.questions}
          onClose={() => setPreview(null)}
        />
      )}
    </div>
  );
};

export default LDQuizEditor;
